import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Dialog } from '@headlessui/react'
import Button from '@atlaskit/button'
import TextField from '@atlaskit/textfield'
import { createLibraryFolderAsync } from '../../store/slices/librarySlice'
import { useNotification } from '../../contexts/NotificationContext'

/**
 * CREATE LIBRARY FOLDER MODAL
 *
 * Modal dialog for creating a new folder in the library.
 * Library folders do not belong to any release.
 */
function CreateLibraryFolderModal({
  isOpen,
  onClose,
  parentFolderId = null,
  jiraProjectId
}) {
  const dispatch = useDispatch()
  const { showError, showWarning } = useNotification()

  // Form state
  const [name, setName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Handle form submission
  const handleSubmit = async () => {
    if (!name.trim()) {
      showWarning('Validation Error', 'Please enter a folder name')
      return
    }

    setIsSubmitting(true)

    try {
      await dispatch(createLibraryFolderAsync({
        name: name.trim(),
        parentId: parentFolderId,
        jiraProjectId,
        orderIndex: null // Backend calculates orderIndex when not provided
      })).unwrap()

      // Reset form and close modal
      resetForm()
      onClose()
    } catch (error) {
      console.error('Failed to create library folder:', error)
      const errorMessage = error?.message || 'Failed to create folder. Please try again.'
      showError('Create Folder Failed', errorMessage)
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Reset form state
  const resetForm = () => {
    setName('')
  }

  // Handle modal close
  const handleClose = () => {
    resetForm()
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className="headless-dialog-root">
      {/* Backdrop */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none'
        }}
      >
        <Dialog.Panel
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            width: '400px',
            maxHeight: '90vh',
            overflow: 'auto',
            pointerEvents: 'auto'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid var(--ds-border, #DFE1E6)'
            }}
          >
            <Dialog.Title
              style={{
                fontSize: '20px',
                fontWeight: 500,
                color: 'var(--ds-text, #172B4D)',
                margin: 0
              }}
            >
              {parentFolderId ? 'Create Sub-folder' : 'Create Library Folder'}
            </Dialog.Title>
          </div>

          {/* Body */}
          <div style={{ padding: '20px 24px' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--ds-text-subtle, #5E6C84)',
                  marginBottom: '8px'
                }}
              >
                Folder Name *
              </label>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && name.trim() && !isSubmitting) {
                    e.preventDefault()
                    handleSubmit()
                  }
                }}
                placeholder="e.g., Authentication Templates"
                isRequired
                autoFocus
              />
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}
          >
            <Button
              appearance="subtle"
              onClick={handleClose}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={!name.trim()}
            >
              Create Folder
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export { CreateLibraryFolderModal }
